type SinkCapableAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
type PlayOpts = { loop?: boolean };

// unlock() で鳴らす無音の WAV (8kHz mono 16bit・16ms)。data URI にしてあるのは、
// unlock() が同期でなければならず fetch も Blob 生成も挟めないため。
// **ミュートしない** — iOS はミュート再生をジェスチャ無しでも許可するので、
// ミュートしたまま鳴らしても「音を出す許可」は得られない。
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQBAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

/**
 * TtsPlayer — 合成音声 Blob を Audio API で再生するクラス。
 * play(blob) は再生完了 (ended) もしくは stop() 呼び出しで resolve する。
 *
 * **不変条件: Audio 要素は 1 つを作って使い回す (作り直さない)。**
 * iOS (WebKit) の autoplay 許可は <audio> **要素単位**で、その要素の play() が
 * ユーザー操作のコールスタック内で呼ばれたときにだけ与えられる。同じページで
 * 一度ジェスチャ内の再生に成功していても、**新しく作った要素**を await の後に
 * play() すると NotAllowedError になる (2026-08-04 実機実測、
 * docs/measurements/2026-08-04-ios-webkit-audio-diagnosis.md の T1/T2/T3)。
 * 読み上げは必ず `await synthesizeSpeech()` の後に鳴らすので、要素を作り直すと
 * iPhone では 1 度も音が出ない。
 */
export class TtsPlayer {
  /** 再生に使う Audio 要素。一度作ったら使い回す (unlock() を保つため)。 */
  private audio: HTMLAudioElement | null = null;
  /** Blob から生成した Object URL。cleanup 時に revoke する。 */
  private url: string | null = null;
  /** play() の Promise を resolve するコールバック。 */
  private resolver: (() => void) | null = null;
  /** 出力先 deviceId。null なら未指定 (OS 既定)。"" は「既定へ戻す」の明示指定。 */
  private sinkId: string | null = null;
  /**
   * pause() 中かどうか。pending な play() が AbortError で reject されたとき、
   * それが「意図した一時停止」なのか本当の失敗なのかを見分けるために持つ。
   */
  private paused = false;

  /** 現在再生中かどうかを返す。 */
  isPlaying(): boolean {
    return this.audio !== null && !this.audio.paused;
  }

  /**
   * 現在の再生位置 (秒)。`tts.spoken` の `audibleMs` (実際に耳へ届いた長さ) の材料。
   *
   * **壁時計では代用できない** — 割り込みの保持 (`pause()` 〜 `resume()`、最大
   * `HOLD_TIMEOUT_MS` = 6 秒) がそのまま乗るので、「どこまで聞こえたか」の答えにならない。
   *
   * `cleanup()` が Audio 要素を捨てない (クラス冒頭の不変条件) ため、`play()` から
   * 戻った直後に読めば `stop()` された位置がそのまま取れる。要素が無い環境や
   * `currentTime` が数値でない場合は 0 に倒す (原則 5 — 観測のために本編を止めない)。
   */
  positionSec(): number {
    const at = this.audio?.currentTime;
    return typeof at === "number" && Number.isFinite(at) ? at : 0;
  }

  /**
   * iOS 向けに再生をアンロックする。**ユーザー操作のハンドラから同期的に呼ぶこと** —
   * await を 1 つでも挟むとジェスチャの文脈が切れ、何の効果も無くなる。
   *
   * ここで作った要素を以後の play() が使い回すことで、fetch を挟んだ読み上げでも
   * 音が出るようになる (クラス冒頭の不変条件)。
   *
   * 再生中は何もしない (「もう一度試す」等で二度呼ばれても読み上げを切らない)。
   * 失敗しても投げない — アンロックできない環境でも本編は進む (原則 5)。
   */
  unlock(): void {
    if (this.resolver) return;
    const audio = (this.audio ??= new Audio() as SinkCapableAudio);
    audio.src = SILENT_WAV;
    void Promise.resolve(audio.play()).catch(() => undefined);
  }

  /**
   * 出力デバイスを切り替える (Chromium のみ)。field に保持して以後の play() に適用し、
   * 再生中なら現行 audio にも即適用する。"" は OS 既定へ戻す (HTMLMediaElement 仕様)。
   * 非対応環境 (Safari/iOS) は黙って skip (出す/出さないの UI 分岐は
   * supportsOutputSelection() 側で行う)。
   * デバイス抜去等で setSinkId が失敗しても既定出力で再生継続 (fire-and-forget)。
   */
  async setSinkId(deviceId: string): Promise<void> {
    this.sinkId = deviceId;
    const audio = this.audio as SinkCapableAudio | null;
    if (audio && typeof audio.setSinkId === "function") {
      await audio.setSinkId(deviceId).catch(() => undefined);
    }
  }

  /**
   * Blob を受け取って再生を開始する。
   * 既に再生中の場合は先に stop() して古い再生を破棄してから開始する。
   * 再生完了 (ended) または stop() 呼び出しで resolve する。
   * loop: true のときは ended が発火しないため stop() のみが終了経路になる。
   * ブラウザの play() がエラーを返した場合は reject する。
   */
  play(blob: Blob, opts: PlayOpts = {}): Promise<void> {
    // 二重再生を防ぐため、既存の再生を先に停止する
    this.stop();

    const url = URL.createObjectURL(blob);
    // 要素は作り直さず使い回す (iOS 対応、クラス冒頭の不変条件を参照)。
    const audio = (this.audio ??= new Audio() as SinkCapableAudio);
    audio.src = url;
    // loop=true のとき ended は発火しない → stop() のみが終了経路 (Preflight Step 3)
    audio.loop = opts.loop ?? false;
    this.url = url;
    // sinkId が "" (既定へ戻した) / null (未指定) のときは呼ばない — 新規 Audio は
    // 元々 OS 既定 sink なので、truthy な deviceId のときだけ適用すれば足りる
    if (this.sinkId && typeof audio.setSinkId === "function") {
      // 適用失敗 (デバイス抜去等) は既定出力で再生継続 (fire-and-forget)
      void audio.setSinkId(this.sinkId).catch(() => undefined);
    }

    return new Promise<void>((resolve, reject) => {
      this.resolver = resolve;

      audio.onended = () => {
        // 再生が自然終了したら cleanup して resolve する
        this.finish();
      };

      audio.onerror = () => {
        this.cleanup();
        reject(new Error("音声の再生に失敗しました"));
      };

      audio.play().catch((err: unknown) => {
        // pause() 直後の AbortError は「意図した停止」なので握りつぶす。ここで
        // cleanup() すると audio と blob URL の両方が消え、resume() で続きから
        // 鳴らせなくなる (spec 2026-07-28 §3.3 不変条件 3)。
        // stop() 経路でこれが表面化しないのは、audio.pause() の直後に同期で
        // finish() が resolve し、後から来る reject が no-op になるため。
        // pause() は finish() を呼ばないので、そのマスクが外れる。
        if (this.paused) return;
        this.cleanup();
        reject(err);
      });
    });
  }

  /**
   * 再生を即座に停止する。
   * play() が返した Promise は resolve される（reject ではない）。
   */
  stop(): void {
    this.paused = false;
    if (this.audio) {
      this.audio.pause();
    }
    // pause 後に finish で resolver を呼ぶ
    this.finish();
  }

  /**
   * 再生位置を保持したまま止める。stop() と違い finish() も cleanup() も呼ばないので、
   * play() が返した Promise は保留のまま残り、resume() で続きから再生できる。
   *
   * 呼び出し側 (useNarration の読み上げループ) がこの Promise を await しているため、
   * ここで resolve するとキューが次のメッセージへ進んでしまう
   * (spec 2026-07-28 §3.3 不変条件 1)。
   */
  pause(): void {
    if (!this.audio) return;
    this.paused = true;
    this.audio.pause();
  }

  /**
   * pause() した再生を続きから再開する。rewindSec だけ手前に戻してから鳴らすと、
   * 止まった瞬間に発音されかけていた語を聞き直せる (spec 2026-07-28 D5)。
   *
   * 同じ Audio 要素を使い続けるのが要点 — 新しく作ると onended / onerror / resolver の
   * 紐付け (play() の executor 内にしかない) が失われ、再生完了を検知できなくなる。
   * さらに sinkId が "" (既定へ戻す明示指定) のとき play() のゲートに弾かれて
   * 前の出力先が残る (spec §3.3 不変条件 2)。
   *
   * 再生に失敗したら stop() に倒して Promise を resolve する。読み上げループが
   * await で永久に止まるのを防ぐ方が、再開できないことを伝えるより優先される (原則 5)。
   */
  resume(rewindSec = 0): void {
    const audio = this.audio;
    // 保留中の play() が無ければ何もしない。要素を使い回すようになったため
    // 「audio があるか」では再生の途中かを判定できず、stop() で捨てたはずの
    // 読み上げを後から鳴らし直してしまう (resolver の有無が唯一の判定材料)。
    if (!audio || !this.resolver) return;
    this.paused = false;
    if (rewindSec > 0) {
      const at = audio.currentTime;
      if (Number.isFinite(at)) audio.currentTime = Math.max(0, at - rewindSec);
    }
    void Promise.resolve(audio.play()).catch(() => this.stop());
  }

  /**
   * resolver を呼び出してから cleanup する。
   * onended / stop() どちらのパスでも共通して使う。
   */
  private finish(): void {
    const resolver = this.resolver;
    this.cleanup();
    resolver?.();
  }

  /**
   * ハンドラ・Object URL・resolver を解放する。
   * URL.revokeObjectURL を忘れると Blob の参照が残り続けるためここで必ず呼ぶ。
   *
   * **Audio 要素そのものは捨てない** — iOS のアンロックは要素単位なので、
   * ここで null にすると次の play() が要素を作り直し、アンロックを失う
   * (クラス冒頭の不変条件)。
   */
  private cleanup(): void {
    if (this.audio) {
      this.audio.onended = null;
      this.audio.onerror = null;
    }
    if (this.url) {
      URL.revokeObjectURL(this.url);
      this.url = null;
    }
    this.resolver = null;
  }
}
